/**
 * React Query Client Configuration
 * 
 * This file configures the React Query client with optimized settings for:
 * - Data caching and stale time management
 * - Error handling and retry logic
 * - Performance optimizations
 * 
 * @fileoverview Centralized query client configuration for the FinMatrix application
 * @author FinMatrix Development Team
 * @version 1.0.0
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Creates a new QueryClient instance
 * 
 * @description This function creates a new QueryClient with performance optimizations.
 * It should be called on the client side to avoid serialization issues.
 * 
 * @returns {QueryClient} A new QueryClient instance
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Data is considered fresh for 5 minutes
        staleTime: 5 * 60 * 1000, // 5 minutes
        
        // Cache data for 10 minutes before garbage collection
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
        
        // Retry failed requests once
        retry: 1,
        
        // Disable automatic refetch on window focus for better UX
        refetchOnWindowFocus: false,
        
        // Disable refetch on component mount if data exists
        refetchOnMount: false,
      },
      mutations: {
        // Retry failed mutations once
        retry: 1,
      },
    },
  });
}

/**
 * Global QueryClient instance (created on client side)
 * This will be initialized in the ClientLayout component
 */
export let queryClient: QueryClient | undefined;

/**
 * Initialize the global QueryClient
 * This should be called on the client side
 */
export function initializeQueryClient(): QueryClient {
  if (!queryClient) {
    queryClient = createQueryClient();
  }
  return queryClient;
}
